import api from '../../services/api'
import { addErrorToast, addSaveToast } from './companyActionCreators'

import {
  TL_RECON_LOADING,
  TL_RECON_REPORT_LOADED,
  TL_RECON_REPORT_FAILED,
  TL_RECON_EXECUTING,
  TL_RECON_EXECUTED,
  TL_RECON_EXECUTE_FAILED,
  TL_RECON_ROW_UPDATING,
  TL_RECON_ROW_UPDATED,
  TL_RECON_ROW_UPDATE_FAILED,
  TL_RECON_ROW_APPROVED,
  TL_RECON_ROW_RESOLVED,
  TL_RECON_MAPPINGS_LOADED,
  TL_RECON_MAPPINGS_FAILED,
  TL_RECON_COMPANIES_LOADED,
  TL_RECON_COMPANIES_FAILED,
  TL_RECON_UNMAPPED_ORGS_LOADED,
  TL_RECON_UNMAPPED_ORGS_FAILED,
} from './threatlockerReconTypes'

export const fetchReport = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: TL_RECON_LOADING })

      const res = await api.get('/threatlocker/recon/report')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      dispatch({ type: TL_RECON_REPORT_LOADED, payload: data })
    } catch (err) {
      dispatch({ type: TL_RECON_REPORT_FAILED })
      dispatch(addErrorToast('Failed to load Threatlocker report'))
    }
  }
}

export const refreshReportSilent = () => {
  return async (dispatch) => {
    try {
      const res = await api.get('/threatlocker/recon/report')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      dispatch({ type: TL_RECON_REPORT_LOADED, payload: data })
    } catch (err) {
      // Silent — don't show error toast for background refresh
    }
  }
}

export const executeRecon = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: TL_RECON_EXECUTING })

      const res = await api.post('/threatlocker/recon/execute')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const result = await res.json()
      dispatch({ type: TL_RECON_EXECUTED, payload: result })
      dispatch(addSaveToast(`Reconciled ${result.productsReconciled} product(s)`))

      dispatch(refreshReportSilent())
    } catch (err) {
      dispatch({ type: TL_RECON_EXECUTE_FAILED })
      dispatch(addErrorToast('Failed to execute auto-reconciliation'))
    }
  }
}

export const approveCompany = (companyId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TL_RECON_ROW_UPDATING, payload: companyId })

      const res = await api.post(`/threatlocker/recon/${companyId}/approve`)
      if (!res.ok) {
        const msg = await res.text()
        throw new Error(msg || `HTTP ${res.status}`)
      }

      dispatch({ type: TL_RECON_ROW_APPROVED, payload: companyId })
      dispatch(addSaveToast('Company approved'))
    } catch (err) {
      dispatch({ type: TL_RECON_ROW_UPDATE_FAILED, payload: companyId })
      dispatch(addErrorToast(err.message || 'Failed to approve'))
    }
  }
}

export const resolveCompany = (companyId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TL_RECON_ROW_UPDATING, payload: companyId })

      const res = await api.post(`/threatlocker/recon/${companyId}/resolve`)
      if (!res.ok) {
        const msg = await res.text()
        throw new Error(msg || `HTTP ${res.status}`)
      }

      const result = await res.json()

      if (result.warnings && result.warnings.length > 0) {
        dispatch(addErrorToast(`Some orgs still blocked: ${result.warnings.join('; ')}`))
        dispatch({ type: TL_RECON_ROW_UPDATE_FAILED, payload: companyId })
      } else {
        const resolved = result.companiesMatched || 0
        dispatch({ type: TL_RECON_ROW_RESOLVED, payload: companyId })
        if (resolved > 0) {
          dispatch(addSaveToast(`Resolved ${resolved} org(s)`))
        }
      }
    } catch (err) {
      dispatch({ type: TL_RECON_ROW_UPDATE_FAILED, payload: companyId })
      dispatch(addErrorToast(err.message || 'Failed to resolve'))
    }
  }
}

export const fetchOrgMappings = () => {
  return async (dispatch) => {
    try {
      const res = await api.get('/threatlocker/recon/org-mapping')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      dispatch({ type: TL_RECON_MAPPINGS_LOADED, payload: data })
    } catch (err) {
      dispatch({ type: TL_RECON_MAPPINGS_FAILED })
      dispatch(addErrorToast('Failed to load org mappings'))
    }
  }
}

export const saveOrgMapping = (threatlockerOrgId, companyId) => {
  return async (dispatch) => {
    try {
      const res = await api.post('/threatlocker/recon/org-mapping', {
        ThreatlockerOrgId: threatlockerOrgId,
        CompanyId: companyId,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      dispatch(addSaveToast('Org mapping saved'))
      dispatch(fetchOrgMappings())
      dispatch(fetchUnmappedOrgs())
      dispatch(refreshReportSilent())
    } catch (err) {
      dispatch(addErrorToast('Failed to save org mapping'))
    }
  }
}

export const deleteOrgMapping = (threatlockerOrgId) => {
  return async (dispatch) => {
    try {
      const res = await api.deleteCall(`/threatlocker/recon/org-mapping/${encodeURIComponent(threatlockerOrgId)}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      dispatch(addSaveToast('Org mapping deleted'))
      dispatch(fetchOrgMappings())
      dispatch(fetchUnmappedOrgs())
      dispatch(refreshReportSilent())
    } catch (err) {
      dispatch(addErrorToast('Failed to delete org mapping'))
    }
  }
}

export const fetchCompanyLabels = () => {
  return async (dispatch) => {
    try {
      const res = await api.get('/threatlocker/recon/companies')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      dispatch({ type: TL_RECON_COMPANIES_LOADED, payload: data })
    } catch (err) {
      dispatch({ type: TL_RECON_COMPANIES_FAILED })
      dispatch(addErrorToast('Failed to load company list'))
    }
  }
}

export const fetchUnmappedOrgs = () => {
  return async (dispatch) => {
    try {
      const res = await api.get('/threatlocker/recon/unmapped-orgs')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      dispatch({ type: TL_RECON_UNMAPPED_ORGS_LOADED, payload: data })
    } catch (err) {
      dispatch({ type: TL_RECON_UNMAPPED_ORGS_FAILED })
    }
  }
}
